/* eslint-disable @typescript-eslint/no-explicit-any */
import { PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DownloadBatch,
  DownloadOrigin,
  LaunchPickerOptions,
  OpenCloudLocationChooserOptions,
  PickerMode,
  PickerPurpose,
  SharePointContext,
} from "./SharePointContext";
import {
  getFolderPathFromPathname,
  getWorkspaceIdFromPathname,
} from "../utils/folderUtils";
import useUserPreference from "@/hooks/useUserPreference";
import { CloudLocationPickerDialog } from "@/pages/workspace/components/CloudLocationPickerDialog";
import {
  CloudLocation,
  ONE_DRIVE_LOCATION,
} from "@/models/microsoft/CloudLocation";
import {
  buildFilePickerConfig,
  buildFilePickerUrl,
  resolvePickerEndpoints,
} from "@/pages/workspace/lib/filePickerConfig";
import {
  ensureInitialized,
  getSharePointUrl,
  getToken,
  hasCachedAccount,
  normalizeTokenType,
  TokenCommand,
  tryGetTokenSilently,
} from "@/services/microsoft/auth";
import { classifyPickerMessage } from "@/pages/workspace/lib/pickerMessages";
import { WorkspaceDocument } from "@/models/workspace/Workspace";
import { uuid } from "@capacit-com/lara-law-shared-ui/uuid";
import {
  showErrorToast,
  showInfoToast,
  showInfoToastWithAction,
  showLoadingToast,
} from "@/components/custom-toasts/toasts";
import { toast } from "@capacit-com/lara-law-shared-ui";
import { UploadSource } from "@/components/upload/types/upload-item";
import { logger } from "@/lib/logger";
import { fetchGraphPdfBlob } from "../lib/graphPdf";
import { graphFetch } from "@/services/microsoft/graphFetch";
import {
  GraphCallOptions,
  fetchLinkedFileForSync,
} from "@/services/microsoft/graphApiService";
import { CloudFileSyncFetch } from "@/types/sync.types";

export type DownloadedFileWithIds = {
  driveId?: string;
  siteId?: string;
  sharepointFileId?: string;
  source: UploadSource;
  file: File;
  folderPath?: string; // Relative path from selected folder root (e.g., "folder1/subfolder/file.pdf")
};

export type DownloadedFolderWithIds = {
  driveId?: string;
  siteId?: string;
  sharepointFileId?: string;
  source: UploadSource;
  folderPath: string; // Relative path from selected folder root (e.g., "folder1/subfolder")
  name: string;
};

const SharePointProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [selectedFolder, setSelectedFolder] = useState<{
    id: string;
    name: string;
    driveId?: string;
    siteId?: string;
  } | null>(null);
  const [baseUrl, setBaseUrl] = useState<string | null>(null);
  const [downloadQueue, setDownloadQueue] = useState<DownloadBatch[]>([]);

  // The "Vælg placering" chooser is owned here so there is exactly one instance
  // regardless of how many entry points (toolbar, row menus, sync dialog) exist.
  const { rememberSharePointSite } = useUserPreference();
  const [pendingChooser, setPendingChooser] = useState<{
    pickerMode: PickerMode;
    purpose: PickerPurpose;
    onCancel?: () => void;
  } | null>(null);

  const pickerWindow = useRef<Window | null>(null);
  const portRef = useRef<MessagePort | null>(null);
  const pickerModeRef = useRef<"file" | "folder">("file");
  const pickerPurposeRef = useRef<"upload" | "selection">("upload"); // Track if picker is for upload or folder selection
  const uuidRef = useRef<string | null>(null);
  const downloadingToastIdRef = useRef<string | number | null>(null);
  // Pin each download to the route it was started from. This provider sits
  // above the router, so a download keeps running while the user navigates and
  // the route alone is no longer a safe answer to "where do these files go?".
  // `routeLocation` (not the `location` name — that is a CloudLocation in this
  // file) read through a ref, because the picker's message handlers are
  // long-lived closures that must see the route as it is when they run.
  const routeLocation = useLocation();
  const locationRef = useRef(routeLocation);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  const monitorIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  useEffect(() => {
    locationRef.current = routeLocation;
  }, [routeLocation]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  /**
   * Records the workspace and folder the user was looking at when they picked,
   * so the upload lands there even if they navigate away while it downloads.
   */
  /**
   * Records the workspace and folder the user was looking at when they picked,
   * so the upload lands there even if they navigate away while it downloads.
   */
  const captureDownloadOrigin = (): DownloadOrigin => {
    const pathname = locationRef.current.pathname;
    return {
      workspaceId: getWorkspaceIdFromPathname(pathname),
      pathname,
    };
  };

  /**
   * Queues a finished download against the route it was started from.
   *
   * When the user has moved on, the upload cannot run yet — it would land in
   * the wrong workspace — so we say so and offer a way back. The toast has no
   * timeout: this is something the user has to come back to, not read in
   * passing. It is dismissed automatically once the upload starts.
   */
  const enqueueDownloadBatch = (
    files: DownloadedFileWithIds[],
    origin: DownloadOrigin
  ) => {
    const batchId = uuid();
    const currentWorkspaceId = getWorkspaceIdFromPathname(
      locationRef.current.pathname
    );
    const isElsewhere =
      !!origin.workspaceId && currentWorkspaceId !== origin.workspaceId;

    const folderNames = getFolderPathFromPathname(origin.pathname);
    const destination =
      folderNames.length > 0 ? folderNames.join(" / ") : "workspacet";

    const pendingToastId = isElsewhere
      ? showInfoToastWithAction(
          `${files.length} ${files.length === 1 ? "fil er" : "filer er"} hentet fra SharePoint/OneDrive og venter på at blive uploadet til ${destination}. Uploaden fortsætter, når du er tilbage.`,
          "Upload venter",
          "Gå til placeringen",
          () => navigateRef.current(origin.pathname)
        )
      : null;

    setDownloadQueue((queue) => [
      ...queue,
      { id: batchId, origin, files, pendingToastId },
    ]);
  };

  const dequeueDownloadBatch = (batchId: string) => {
    setDownloadQueue((queue) => queue.filter((batch) => batch.id !== batchId));
  };

  const clearWindowMonitor = () => {
    if (monitorIntervalRef.current !== null) {
      clearInterval(monitorIntervalRef.current);
      monitorIntervalRef.current = null;
    }
  };

  const closePickerSession = () => {
    clearWindowMonitor();
    portRef.current?.close();
    portRef.current = null;
    if (pickerWindow.current && !pickerWindow.current.closed) {
      pickerWindow.current.close();
    }
    pickerWindow.current = null;
    uuidRef.current = null;
  };

  const buildGraphApiItemUrl = (
    driveId: string | undefined,
    siteId: string | undefined,
    itemId: string,
    path: string = ""
  ): string => {
    const basePath = path ? `/${path}` : "";
    if (driveId) {
      return `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}${basePath}`;
    }
    if (siteId) {
      return `https://graph.microsoft.com/v1.0/sites/${siteId}/drive/items/${itemId}${basePath}`;
    }
    throw new Error("Either driveId or siteId must be provided");
  };

  const fetchBlob = async (url: string, token: string) => {
    const response = await graphFetch(
      url,
      token,
      { operation: "graphFetchBlob" },
      { headers: { Accept: "application/json" } }
    );
    return response.blob();
  };

  const fetchJson = async (url: string, token: string) => {
    const response = await graphFetch(
      url,
      token,
      { operation: "graphFetchJson" },
      { headers: { Accept: "application/json" } }
    );
    return response.json();
  };

  const fetchAllPages = async (
    initialUrl: string,
    token: string
  ): Promise<any[]> => {
    const allItems: any[] = [];
    let nextLink: string | null = initialUrl;

    while (nextLink) {
      const response = await fetchJson(nextLink, token);
      if (response.value) {
        allItems.push(...response.value);
      }
      nextLink = response["@odata.nextLink"] || null;
    }

    return allItems;
  };

  const detectFileSource = (
    siteUrl: string,
    driveType?: string
  ): UploadSource => {
    // Use driveType if available (more reliable)
    if (driveType === "personal") {
      return UploadSource.ONEDRIVE;
    }
    if (driveType === "business") {
      // Business drives can be either OneDrive for Business or SharePoint
      // Check siteUrl to distinguish
      return siteUrl.includes("-my.sharepoint.com") ||
        siteUrl.includes("/personal/")
        ? UploadSource.ONEDRIVE
        : UploadSource.SHAREPOINT;
    }
    // Fallback to siteUrl check
    return siteUrl.includes("-my.sharepoint.com") ||
      siteUrl.includes("/personal/")
      ? UploadSource.ONEDRIVE
      : UploadSource.SHAREPOINT;
  };

  // The File Picker correlates results by the exact `id` it sent (number or
  // string). Coercing to string breaks correlation and the picker times out,
  // showing a blank window and eventually reloading itself. Preserve the
  // original primitive type the picker provided.
  const sendResult = (id: string | number, data: unknown) => {
    portRef.current?.postMessage({ type: "result", id, data });
  };

  const dismissDownloadingToast = () => {
    if (downloadingToastIdRef.current !== null) {
      toast.dismiss(downloadingToastIdRef.current);
      downloadingToastIdRef.current = null;
    }
  };

  // const downloadFileAsPdf = async (item: {
  //   id: string;
  //   name: string;
  //   parentReference: any;
  // }): Promise<DownloadedFileWithIds | null> => {
  //   console.debug("[SharePointProvider] downloadFile - Starting download", {
  //     itemId: item.id,
  //     itemName: item.name,
  //     parentReference: item.parentReference,
  //   });

  //   const token = await getToken({
  //     resource: "https://graph.microsoft.com",
  //     type: "Graph",
  //     scopes: ["Files.Read", "Files.Read.All", "User.Read"],
  //   });

  //   if (!token) {
  //     console.error(
  //       "[SharePointProvider] downloadFile - Failed to acquire authentication token"
  //     );
  //     return null;
  //   }

  //   console.debug(
  //     "[SharePointProvider] downloadFile - Token acquired successfully"
  //   );

  //   const { driveId, sharepointIds } = item.parentReference;
  //   if (!driveId || !sharepointIds?.siteId) {
  //     console.error(
  //       "[SharePointProvider] downloadFile - Invalid item structure",
  //       {
  //         driveId,
  //         siteId: sharepointIds?.siteId,
  //         item,
  //       }
  //     );
  //     return null;
  //   }

  //   const source = detectFileSource(sharepointIds.siteUrl ?? "");
  //   const fileDownloadUrl =
  //     source === UploadSource.SHAREPOINT
  //       ? `https://graph.microsoft.com/v1.0/sites/${sharepointIds.siteId}/drive/items/${item.id}/content?format=pdf`
  //       : `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${item.id}/content?format=pdf`;

  //   console.debug(
  //     "[SharePointProvider] downloadFile - Requesting file conversion",
  //     {
  //       source,
  //       fileDownloadUrl,
  //       driveId,
  //       siteId: sharepointIds.siteId,
  //     }
  //   );

  //   try {
  //     // Request file conversion - Microsoft Graph returns a 302 redirect to a preauthenticated URL
  //     // The browser will automatically follow the redirect, so we don't need to handle it manually
  //     // This avoids CORS issues that occur when trying to manually read the Location header
  //     const response = await fetch(fileDownloadUrl, {
  //       method: "GET",
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //       },
  //       // Let the browser automatically follow the 302 redirect
  //       // The preauthenticated URL in the Location header will be fetched automatically
  //     });

  //     console.debug(
  //       "[SharePointProvider] downloadFile - Response received (after redirect)",
  //       {
  //         status: response.status,
  //         statusText: response.statusText,
  //         contentType: response.headers.get("content-type"),
  //         contentLength: response.headers.get("content-length"),
  //         url: response.url, // This will show the final URL after redirect
  //       }
  //     );

  //     if (!response.ok) {
  //       const errorText = await response
  //         .text()
  //         .catch(() => "Unable to read error response");
  //       console.error(
  //         "[SharePointProvider] downloadFile - Failed to download file",
  //         {
  //           status: response.status,
  //           statusText: response.statusText,
  //           errorText,
  //           requestUrl: fileDownloadUrl,
  //           finalUrl: response.url,
  //         }
  //       );
  //       throw new Error(
  //         `Failed to download file: ${response.status} ${response.statusText}`
  //       );
  //     }

  //     const blob = await response.blob();
  //     console.debug(
  //       "[SharePointProvider] downloadFile - File downloaded successfully",
  //       {
  //         blobSize: blob.size,
  //         blobType: blob.type,
  //         fileName: item.name,
  //         finalUrl: response.url,
  //       }
  //     );

  //     // Ensure the file has the correct MIME type (PDF)
  //     const fileType = blob.type || "application/pdf";
  //     return {
  //       file: new File([blob], item.name.replace(/\.[^/.]+$/, ".pdf"), {
  //         type: fileType,
  //       }),
  //       source,
  //       sharepointFileId: item.id,
  //       driveId,
  //       siteId: sharepointIds.siteId,
  //     };
  //   } catch (error) {
  //     console.error(
  //       "[SharePointProvider] downloadFile - Error downloading file",
  //       {
  //         error,
  //         errorMessage: error instanceof Error ? error.message : String(error),
  //         itemId: item.id,
  //         itemName: item.name,
  //       }
  //     );
  //     showErrorToast("Fejl ved download af fil fra SharePoint/OneDrive.");
  //     return null;
  //   }
  // };

  const downloadFile = async (
    item: {
      id: string;
      name: string;
      parentReference: any;
    },
    folderPath?: string,
    providedSource?: UploadSource // Allow source to be provided from parent folder
  ): Promise<DownloadedFileWithIds | null> => {
    const operation = "downloadFile";
    let token: string;
    try {
      token = await getToken({
        resource: "https://graph.microsoft.com",
        type: "Graph",
        scopes: ["Files.Read", "Files.Read.All", "User.Read"],
      });
    } catch (error) {
      logger.error(
        "Failed to acquire Microsoft Graph token for downloading file",
        { operation, error, itemId: item.id }
      );
      showErrorToast(
        "Kunne ikke logge på Microsoft for at hente filen. Prøv at logge ind igen."
      );
      return null;
    }

    const { driveId, sharepointIds, siteId, driveType } = item.parentReference;
    // Handle both structures: siteId can be directly in parentReference or in sharepointIds
    const actualSiteId = siteId || sharepointIds?.siteId;
    const siteUrl = sharepointIds?.siteUrl || item.parentReference.path || "";

    if (!driveId || !actualSiteId) {
      logger.error(
        "Invalid SharePoint/OneDrive metadata on item — missing driveId or siteId",
        {
          operation,
          itemId: item.id,
          hasDriveId: Boolean(driveId),
          hasSiteId: Boolean(actualSiteId),
        }
      );
      showErrorToast(
        `Filen "${item.name}" mangler SharePoint/OneDrive-data og kan ikke hentes.`
      );
      return null;
    }

    // Use provided source if available (from parent folder), otherwise detect from item
    const source =
      providedSource !== undefined
        ? providedSource
        : detectFileSource(siteUrl, driveType);
    const fileDownloadUrl = buildGraphApiItemUrl(
      driveId,
      actualSiteId,
      item.id,
      "content"
    );

    try {
      const blob = await fetchBlob(fileDownloadUrl, token);
      return {
        file: new File([blob], item.name, { type: blob.type }),
        source,
        sharepointFileId: item.id,
        driveId,
        siteId: actualSiteId,
        folderPath,
      };
    } catch (error) {
      logger.error("Error downloading file from SharePoint/OneDrive", {
        operation,
        error,
        itemId: item.id,
        driveId,
        siteId: actualSiteId,
      });
      showErrorToast(
        `Kunne ikke hente filen "${item.name}" fra SharePoint/OneDrive.`
      );
      return null;
    }
  };

  const downloadFolderContents = async (
    folderItem: {
      id: string;
      name: string;
      parentReference: any;
    },
    basePath: string = "",
    parentSource?: UploadSource // Source from parent folder to maintain consistency
  ): Promise<{
    files: DownloadedFileWithIds[];
    folders: DownloadedFolderWithIds[];
  }> => {
    const operation = "downloadFolderContents";
    let token: string;
    try {
      token = await getToken({
        resource: "https://graph.microsoft.com",
        type: "Graph",
        scopes: ["Files.Read", "Files.Read.All", "User.Read"],
      });
    } catch (error) {
      logger.error(
        "Failed to acquire Microsoft Graph token for downloading folder",
        {
          operation,
          error,
          folderId: folderItem.id,
        }
      );
      showErrorToast(
        "Kunne ikke logge på Microsoft for at hente mappen. Prøv at logge ind igen."
      );
      return { files: [], folders: [] };
    }

    const { driveId, sharepointIds, siteId, driveType } =
      folderItem.parentReference;
    // Handle both structures: siteId can be directly in parentReference or in sharepointIds
    const actualSiteId = siteId || sharepointIds?.siteId;
    const siteUrl =
      sharepointIds?.siteUrl || folderItem.parentReference.path || "";

    if (!driveId || !actualSiteId) {
      logger.error(
        "Invalid SharePoint/OneDrive metadata on folder — missing driveId or siteId",
        {
          operation,
          folderId: folderItem.id,
          hasDriveId: Boolean(driveId),
          hasSiteId: Boolean(actualSiteId),
        }
      );
      showErrorToast(
        `Mappen "${folderItem.name}" mangler SharePoint/OneDrive-data og kan ikke hentes.`
      );
      return { files: [], folders: [] };
    }

    const source =
      parentSource !== undefined
        ? parentSource
        : detectFileSource(siteUrl, driveType);

    const childrenUrl = buildGraphApiItemUrl(
      driveId,
      actualSiteId,
      folderItem.id,
      "children"
    );

    try {
      const children = await fetchAllPages(childrenUrl, token);

      const downloadedFiles: DownloadedFileWithIds[] = [];
      const downloadedFolders: DownloadedFolderWithIds[] = [];

      // Process each child item
      for (const child of children) {
        if (child.folder) {
          // Recursively process subfolders, passing down the source
          const subfolderPath = basePath
            ? `${basePath}/${child.name}`
            : child.name;
          const subfolderResult = await downloadFolderContents(
            {
              id: child.id,
              name: child.name,
              parentReference: child.parentReference,
            },
            subfolderPath,
            source // Pass source down to maintain consistency
          );
          downloadedFiles.push(...subfolderResult.files);
          downloadedFolders.push(...subfolderResult.folders);

          // Add the subfolder itself to folders list
          // Empty folders are already added by the recursive call, but we need to add
          // non-empty folders to preserve the folder structure
          const {
            driveId: childDriveId,
            sharepointIds: childSharepointIds,
            siteId: childSiteId,
          } = child.parentReference;
          const childActualSiteId = childSiteId || childSharepointIds?.siteId;

          // Check if this folder was already added by the recursive call (for empty folders)
          const alreadyAdded = subfolderResult.folders.some(
            (f) =>
              f.folderPath === subfolderPath && f.sharepointFileId === child.id
          );

          // Only add if not already added (empty folders are added by recursive call)
          if (!alreadyAdded) {
            downloadedFolders.push({
              driveId: childDriveId,
              siteId: childActualSiteId,
              sharepointFileId: child.id,
              source,
              folderPath: subfolderPath,
              name: child.name,
            });
          }
        } else {
          // Download file, passing the source to ensure consistency
          // folderPath should only be the parent folder path, not include the filename
          const downloadedFile = await downloadFile(
            {
              id: child.id,
              name: child.name,
              parentReference: child.parentReference,
            },
            basePath || undefined, // Pass only the parent folder path, not the full file path
            source // Pass source to maintain consistency with root folder
          );
          if (downloadedFile) {
            downloadedFiles.push(downloadedFile);
          }
        }
      }

      // If folder is empty (no children), add it to folders list
      // This handles empty folders at any level
      if (children.length === 0) {
        const folderPath = basePath || folderItem.name;
        downloadedFolders.push({
          driveId,
          siteId: actualSiteId,
          sharepointFileId: folderItem.id,
          source,
          folderPath: folderPath,
          name: folderItem.name,
        });
      }

      return { files: downloadedFiles, folders: downloadedFolders };
    } catch (error) {
      logger.error(
        "Error downloading folder contents from SharePoint/OneDrive",
        {
          operation,
          error,
          folderId: folderItem.id,
          driveId,
          siteId: actualSiteId,
        }
      );
      showErrorToast(
        `Kunne ikke hente indholdet af mappen "${folderItem.name}" fra SharePoint/OneDrive.`
      );
      return { files: [], folders: [] };
    }
  };

  const fetchGraphPdfForCloudItem = async (ids: {
    driveId: string;
    siteId: string;
    sharepointFileId: string;
  }): Promise<Blob | null> => {
    const operation = "fetchGraphPdfForCloudItem";
    let token: string;
    try {
      token = await getToken({
        resource: "https://graph.microsoft.com",
        type: "Graph",
        scopes: ["Files.Read", "Files.Read.All", "User.Read"],
      });
    } catch (error) {
      logger.error(
        "Failed to acquire Microsoft Graph token for PDF conversion",
        {
          operation,
          error,
          driveId: ids.driveId,
          itemId: ids.sharepointFileId,
        }
      );
      return null;
    }

    return fetchGraphPdfBlob(
      ids.driveId,
      ids.siteId,
      ids.sharepointFileId,
      token
    );
  };

  /**
   * Fetches a linked document's metadata and downloads its content only when
   * `shouldDownload` says the cloud copy is worth it (newer, or the search
   * index needs repair). Throttling and outages come back as `failed`, never
   * `missing`: only a 404, 403 or other 4xx says something about the file.
   */
  const fetchFileForSync = async (
    document: WorkspaceDocument,
    shouldDownload: (cloudLastModified: number) => boolean,
    graphOptions: GraphCallOptions = {}
  ): Promise<CloudFileSyncFetch> => {
    const operation = "downloadFileById";
    let token: string;
    try {
      token = await getToken({
        resource: "https://graph.microsoft.com",
        type: "Graph",
        scopes: ["Files.Read", "Files.Read.All", "User.Read"],
      });
    } catch (error) {
      logger.error(
        "Failed to acquire Microsoft Graph token for downloadFileById",
        {
          operation,
          error,
          documentId: document.id,
        }
      );
      // An auth failure says nothing about the file: report it as failed so
      // the sync never offers to delete the document.
      showErrorToast(
        "Kunne ikke logge på Microsoft for at hente filen. Prøv at logge ind igen."
      );
      return { kind: "failed", reason: "kunne ikke logge på Microsoft" };
    }

    const sharepointFileId = document.sharepointFileId;
    if (!sharepointFileId || (!document.driveId && !document.siteId)) {
      logger.error(
        "Invalid SharePoint/OneDrive identifiers on document — cannot build Graph URL",
        {
          operation,
          documentId: document.id,
          hasDriveId: Boolean(document.driveId),
          hasSiteId: Boolean(document.siteId),
          hasSharepointFileId: Boolean(document.sharepointFileId),
        }
      );
      return { kind: "missing" };
    }

    return fetchLinkedFileForSync(
      {
        driveId: document.driveId,
        siteId: document.siteId,
        sharepointFileId,
      },
      document.name,
      token,
      shouldDownload,
      { ...graphOptions, documentId: document.id }
    );
  };

  const messageListener = async (message: MessageEvent) => {
    const classified = classifyPickerMessage(message.data);

    const pickerContext = {
      pickerMode: pickerModeRef.current,
      purpose: pickerPurposeRef.current,
    };

    if (classified.kind === "notification") {
      // Notifications need no reply. `page-loaded` marks the picker as ready;
      // `isExpected: false` ones are the only evidence we get of problems
      // inside the picker (render failures, auth trouble), so keep their payload.
      if (classified.isError) {
        logger.warn("SharePoint picker reported a problem", {
          operation: "pickerNotification",
          notification: classified.name,
          payload: classified.payload,
          ...pickerContext,
        });
      } else if (classified.name === "page-loaded") {
        logger.info("SharePoint picker page loaded", {
          operation: "pickerNotification",
          ...pickerContext,
        });
      }
      return;
    }

    if (classified.kind === "unknown") {
      logger.warn("Unknown message shape received on SharePoint picker port", {
        operation: "messageListener",
        messageType: classified.messageType,
      });
      return;
    }

    const id = classified.id;
    if (id === null) {
      // Only a malformed command can lack an id; nothing to acknowledge.
      logger.warn("Malformed SharePoint picker command envelope (no id)", {
        operation: "messageListener",
      });
      return;
    }

    // File Picker requires an immediate acknowledge for every command message.
    // If we validate before ack, malformed or unexpected-but-valid shapes cause
    // acknowledgeTimeout in the hosting surface (e.g. Teams).
    // Always echo the picker's original `id` value/type (do NOT coerce) — both
    // for `acknowledge` and for the eventual `result` — otherwise the picker
    // can't correlate our reply.
    portRef.current?.postMessage({ type: "acknowledge", id });

    if (classified.kind === "malformedCommand") {
      logger.warn("Malformed SharePoint picker command", {
        operation: "messageListener",
        commandId: String(id),
        reason: classified.reason,
        keys: classified.keys,
      });
      sendResult(id, {
        result: "error",
        error: {
          code: "malformedCommand",
          message:
            classified.reason === "noData"
              ? "Command payload is missing or invalid"
              : "Command name is missing",
        },
      });
      return;
    }

    const command = classified.command;
    const items: any[] = Array.isArray(command.items) ? command.items : [];

    const commandHandlers: Record<string, () => Promise<void>> = {
      authenticate: async () => {
        // The picker's IAuthenticateCommand: { command, resource, type, claimsChallenge?: { claims } }.
        // `type` is "SharePoint" | "Graph" in Microsoft's samples but the live
        // picker also sends e.g. "SharePoint_MSAL"; normalise rather than reject.
        const tokenType = normalizeTokenType(command.type);
        if (tokenType === null || typeof command.resource !== "string") {
          logger.warn("SharePoint picker authenticate command not understood", {
            operation: "pickerAuthenticate",
            tokenType: String(command.type),
            hasResource: typeof command.resource === "string",
          });
          sendResult(id, {
            result: "error",
            error: { code: "authFailed", message: "unsupported token type" },
          });
          return;
        }

        const challenge = command.claimsChallenge;
        const claims =
          typeof challenge === "object" &&
          challenge !== null &&
          typeof (challenge as { claims?: unknown }).claims === "string"
            ? (challenge as { claims: string }).claims
            : undefined;

        const tokenCommand: TokenCommand = {
          type: tokenType,
          resource: command.resource,
          claimsChallenge: claims,
        };

        // Relayed requests arrive without a user gesture, so a popup would be
        // blocked anyway: serve them from MSAL's silent cache only. The picker
        // also asks for optional resources we hold no permission for (e.g.
        // loki.delve.office.com for its insights pivots); `authFailed` lets it
        // degrade those pivots instead of surfacing a host error. No toast.
        try {
          const token = await tryGetTokenSilently(tokenCommand);
          if (!token) {
            logger.warn("SharePoint picker token request not served", {
              operation: "pickerAuthenticate",
              tokenType: tokenCommand.type,
              resource: tokenCommand.resource,
            });
            sendResult(id, {
              result: "error",
              error: { code: "authFailed" },
            });
            return;
          }
          sendResult(id, { result: "token", token });
        } catch (error) {
          logger.warn("SharePoint picker token request failed", {
            operation: "pickerAuthenticate",
            tokenType: tokenCommand.type,
            resource: tokenCommand.resource,
            error,
          });
          sendResult(id, {
            result: "error",
            error: { code: "authFailed" },
          });
        }
      },
      close: async () => {
        closePickerSession();
      },
      pick: async () => {
        sendResult(id, { result: "success" });
        closePickerSession();

        // Handle based on picker mode
        if (pickerModeRef.current === "folder") {
          const folders = items;

          if (pickerPurposeRef.current === "selection") {
            // Folder selection mode: just set selected folder (for destination)
            const selectedFolder = folders[0];
            if (selectedFolder) {
              // Deliberately only `parentReference.siteId`: OneDrive items also
              // carry `sharepointIds.siteId`, and downstream code treats a
              // present siteId as "this is SharePoint" (folderSource, upload URL).
              setSelectedFolder({
                id: selectedFolder.id,
                name: selectedFolder.name,
                driveId: selectedFolder.parentReference.driveId,
                siteId: selectedFolder.parentReference.siteId,
              });
            }
          } else {
            // Folder upload mode: download folder contents recursively
            // Show loading toast before starting downloads
            const folderOrigin = captureDownloadOrigin();
            // A local id, not the shared ref: a second pick would overwrite the
            // ref and leave this toast on screen forever.
            const folderToastId = showLoadingToast(
              "Henter filer fra SharePoint/OneDrive...",
              "Henter filer"
            );

            try {
              // Use folder name as basePath so files maintain folder structure
              const allDownloadedFiles: DownloadedFileWithIds[] = [];
              const allDownloadedFolders: DownloadedFolderWithIds[] = [];

              for (const folder of folders) {
                const folderResult = await downloadFolderContents(
                  folder,
                  folder.name
                ).catch((error) => {
                  // Safety net — downloadFolderContents catches internally and
                  // returns an empty result, so this branch should be unreachable.
                  logger.error("Unexpected throw from downloadFolderContents", {
                    operation: "messageListenerPickFolder",
                    error,
                    folderId: folder?.id,
                  });
                  showErrorToast(
                    `Kunne ikke hente indholdet af mappen "${folder?.name ?? "ukendt"}" fra SharePoint/OneDrive.`
                  );
                  return { files: [], folders: [] };
                });
                allDownloadedFiles.push(...folderResult.files);
                allDownloadedFolders.push(...folderResult.folders);

                // Add root folder itself if it's empty or has no files
                const {
                  driveId: rootDriveId,
                  sharepointIds: rootSharepointIds,
                  siteId: rootSiteId,
                } = folder.parentReference;
                const rootActualSiteId =
                  rootSiteId || rootSharepointIds?.siteId;
                const rootSource = detectFileSource(
                  rootSharepointIds?.siteUrl ||
                    folder.parentReference.path ||
                    "",
                  folder.parentReference.driveType
                );
                allDownloadedFolders.push({
                  driveId: rootDriveId,
                  siteId: rootActualSiteId,
                  sharepointFileId: folder.id,
                  source: rootSource,
                  folderPath: folder.name,
                  name: folder.name,
                });
              }

              if (
                allDownloadedFiles.length > 0 ||
                allDownloadedFolders.length > 0
              ) {
                // Store folders metadata in a custom property on files for upload handler
                // If there are no files but there are folders, create a dummy file to carry the folder metadata
                if (
                  allDownloadedFiles.length === 0 &&
                  allDownloadedFolders.length > 0
                ) {
                  // Create a placeholder file to carry empty folder metadata
                  const placeholderFile: DownloadedFileWithIds & {
                    _sharePointFolders: DownloadedFolderWithIds[];
                  } = {
                    file: new File([], "__placeholder__"),
                    source:
                      allDownloadedFolders[0]?.source ||
                      UploadSource.SHAREPOINT,
                    driveId: allDownloadedFolders[0]?.driveId,
                    siteId: allDownloadedFolders[0]?.siteId,
                    sharepointFileId: undefined,
                    folderPath: undefined,
                    _sharePointFolders: allDownloadedFolders,
                  };
                  enqueueDownloadBatch([placeholderFile], folderOrigin);
                } else {
                  const filesWithFolders = allDownloadedFiles.map((file) => ({
                    ...file,
                    _sharePointFolders: allDownloadedFolders,
                  }));
                  enqueueDownloadBatch(
                    filesWithFolders as DownloadedFileWithIds[],
                    folderOrigin
                  );
                }
              }
            } finally {
              // Always dismiss toast when done (success or error)
              toast.dismiss(folderToastId);
            }
          }
        } else {
          // File mode: download individual files
          // Show loading toast before starting downloads
          const fileOrigin = captureDownloadOrigin();
          const fileToastId = showLoadingToast(
            "Henter filer fra SharePoint/OneDrive...",
            "Henter filer"
          );

          try {
            const files = items;
            const downloadedFiles = await Promise.all(
              files.map(
                async (item: {
                  id: string;
                  name: string;
                  parentReference: any;
                }) =>
                  downloadFile(item).catch((error) => {
                    // Safety net — downloadFile catches internally and returns
                    // null, so this branch should be unreachable.
                    logger.error("Unexpected throw from downloadFile", {
                      operation: "messageListenerPickFile",
                      error,
                      itemId: item?.id,
                    });
                    showErrorToast(
                      `Kunne ikke hente filen "${item?.name ?? "ukendt"}" fra SharePoint/OneDrive.`
                    );
                    return null;
                  })
              )
            );

            const validFiles = downloadedFiles.filter(
              (file): file is DownloadedFileWithIds => file !== null
            );
            if (validFiles.length > 0) {
              enqueueDownloadBatch(validFiles, fileOrigin);
            }
          } finally {
            // Always dismiss toast when done (success or error)
            toast.dismiss(fileToastId);
          }
        }
      },
    };

    if (commandHandlers[command.command]) {
      try {
        await commandHandlers[command.command]();
      } catch (error) {
        logger.error("SharePoint picker command handler threw an error", {
          operation: "messageListenerCommandHandler",
          error,
          commandName: command?.command,
        });
        // Ensure toast is dismissed if command handler fails
        dismissDownloadingToast();
        showErrorToast(
          "Der opstod en fejl i SharePoint/OneDrive-vinduet. Prøv igen."
        );
        sendResult(id, {
          result: "error",
          error: {
            code: "commandFailed",
            message: error instanceof Error ? error.message : "Unknown error",
          },
        });
      }
    } else {
      sendResult(id, {
        result: "error",
        error: { code: "unsupportedCommand", message: command.command },
        isExpected: true,
      });
    }
  };

  const launchPicker = async (
    e: React.MouseEvent<HTMLElement> | undefined,
    pickerMode?: "folder" | "file",
    purpose: "upload" | "selection" = "upload",
    options?: LaunchPickerOptions
  ) => {
    e?.preventDefault();
    const operation = "launchPicker";
    const location: CloudLocation = options?.location ?? ONE_DRIVE_LOCATION;
    const siteWebUrl = location.kind === "site" ? location.webUrl : undefined;
    // Support legacy mode parameter, but default to mixed mode for best UX
    pickerModeRef.current = pickerMode || "file";
    pickerPurposeRef.current = purpose;

    // Reset any previous picker session
    closePickerSession();

    // Browsers grant one popup per click. A user without a cached Microsoft
    // account needs the MSAL login popup, so the picker window cannot open
    // from the same gesture: spend this click on the login and ask for another.
    // The check is synchronous on purpose: nothing may await between the
    // user's gesture and `window.open` below.
    if (!hasCachedAccount()) {
      try {
        await getToken({
          resource: "https://graph.microsoft.com",
          type: "Graph",
          scopes: ["Files.Read", "Files.Read.All", "User.Read"],
        });
      } catch (error) {
        logger.error(
          "Failed to link Microsoft account before opening SharePoint picker",
          { operation, error, pickerMode: pickerModeRef.current, purpose }
        );
        showErrorToast(
          "Kunne ikke logge på SharePoint/OneDrive. Prøv at logge ud og ind igen."
        );
        return;
      }
      logger.info("Microsoft account linked — picker needs a second click", {
        operation,
        pickerMode: pickerModeRef.current,
        purpose,
      });
      showInfoToast(
        "Din Microsoft-konto er nu tilknyttet. Klik igen for at åbne SharePoint/OneDrive.",
        "Microsoft-konto tilknyttet"
      );
      return;
    }

    const newUuid = uuid();
    uuidRef.current = newUuid;
    const windowTarget = `Picker_${newUuid}`;

    // Open the window synchronously, inside the click's transient user
    // activation and before any await: an MSAL login popup consumes that
    // activation, and in prod every popup-blocked picker window (35/35 over
    // 30 days) followed an MSAL popup in the same session.
    const win = window.open(
      "/minimal.html",
      windowTarget,
      "width=1000,height=700,resizable=yes"
    );

    if (!win) {
      uuidRef.current = null;
      logger.error(
        "Failed to open SharePoint picker window — likely blocked by popup blocker",
        { operation, pickerMode: pickerModeRef.current, purpose }
      );
      showErrorToast(
        "Browseren blokerede pop-up-vinduet. Tillad pop-ups for dette site og prøv igen."
      );
      return;
    }
    pickerWindow.current = win;

    // Every failure below must close the placeholder window again — but only
    // while this launch still owns the session. A newer launch may have
    // replaced the refs while this one was awaiting; never tear down its state.
    const ownsSession = () => pickerWindow.current === win;
    const abort = () => {
      if (ownsSession()) closePickerSession();
    };

    let authToken: string;
    try {
      authToken = await getToken({
        resource: "https://graph.microsoft.com",
        type: "Graph",
        scopes: ["Files.Read", "Files.Read.All", "User.Read"],
      });
    } catch (error) {
      logger.error(
        "Failed to acquire Microsoft Graph token for SharePoint picker",
        { operation, error, pickerMode: pickerModeRef.current, purpose }
      );
      showErrorToast(
        "Kunne ikke logge på SharePoint/OneDrive. Prøv at logge ud og ind igen."
      );
      abort();
      return;
    }

    // OneDrive lives on the tenant's -my host, derived from the tenant root site.
    // A SharePoint site carries its own web URL and needs no root lookup.
    let localBaseUrl = baseUrl;

    if (location.kind === "oneDrive" && !localBaseUrl) {
      localBaseUrl = await getSharePointUrl(authToken);
      if (!localBaseUrl) {
        logger.error("Failed to resolve SharePoint base URL from Graph API", {
          operation,
          pickerMode: pickerModeRef.current,
          purpose,
        });
        showErrorToast(
          "Kunne ikke finde din SharePoint/OneDrive-konto. Tjek at din konto er korrekt opsat."
        );
        abort();
        return;
      }
      setBaseUrl(localBaseUrl);
    }

    const endpoints = resolvePickerEndpoints(location, localBaseUrl);
    if (!endpoints) {
      logger.error("Could not resolve picker endpoints for cloud location", {
        operation,
        location: location.kind,
        siteWebUrl,
      });
      showErrorToast(
        "Kunne ikke finde din SharePoint/OneDrive-konto. Tjek at din konto er korrekt opsat."
      );
      abort();
      return;
    }

    try {
      authToken = await getToken({
        resource: endpoints.tokenResource,
        type: "SharePoint_SelfIssued",
        scopes: ["AllSites.Read", "MyFiles.Read"],
      });
    } catch (error) {
      logger.error(
        "Failed to acquire self-issued SharePoint token for picker",
        { operation, error, tokenResource: endpoints.tokenResource, siteWebUrl }
      );
      showErrorToast(
        "Kunne ikke logge på din SharePoint/OneDrive-konto. Prøv at logge ud og ind igen."
      );
      abort();
      return;
    }

    const pickerConfig = buildFilePickerConfig({
      pickerMode: pickerModeRef.current,
      channelId: newUuid,
      origin: window.location.origin,
      location,
    });
    // Locale only for site launches so the OneDrive URL stays exactly as before.
    const url = buildFilePickerUrl(
      endpoints.postBaseUrl,
      pickerConfig,
      location.kind === "site" ? "da-dk" : undefined
    );

    logger.info("SharePoint picker opened", {
      operation,
      location: location.kind,
      siteWebUrl,
      siteList: location.kind === "site" ? location.list : undefined,
      siteFolder: location.kind === "site" ? location.folder : undefined,
      pickerMode: pickerModeRef.current,
      purpose,
    });

    // Remember the site only now that the picker is actually opening; the
    // abort paths above must not push a site the user never reached.
    if (location.kind === "site") {
      rememberSharePointSite.mutate({
        webUrl: location.webUrl,
        displayName: location.displayName,
      });
    }

    // The placeholder may be gone: the user closed it while tokens were being
    // acquired, or a newer launch replaced this session. Posting the form now
    // would open a fresh tab under the same target name.
    if (win.closed || !ownsSession()) {
      logger.info("SharePoint picker window gone before load", {
        operation,
        pickerMode: pickerModeRef.current,
        purpose,
        superseded: !ownsSession(),
      });
      abort();
      return;
    }

    monitorIntervalRef.current = setInterval(() => {
      if (!pickerWindow.current || pickerWindow.current.closed) {
        clearWindowMonitor();
        pickerWindow.current = null;
        uuidRef.current = null;
      }
    }, 500);

    setTimeout(() => {
      try {
        const form = document.createElement("form");
        form.method = "POST";
        form.action = url;
        form.target = windowTarget;

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "access_token";
        input.value = authToken;
        form.appendChild(input);

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
      } catch (error) {
        logger.error("Error setting up SharePoint picker window form/post", {
          operation,
          error,
          pickerMode: pickerModeRef.current,
          purpose,
        });
        showErrorToast(
          "Kunne ikke åbne SharePoint/OneDrive-vinduet. Prøv igen."
        );
        abort();
      }
    }, 10);
  };

  const uploadFileToSharePoint = async (file: File): Promise<any | null> => {
    const operation = "uploadFileToSharePoint";
    let token: string;
    try {
      token = await getToken({
        resource: "https://graph.microsoft.com",
        type: "Graph",
        scopes: ["Files.ReadWrite", "Files.ReadWrite.All"],
      });
    } catch (error) {
      logger.error(
        "Failed to acquire Microsoft Graph token for SharePoint upload",
        { operation, error, fileSize: file.size }
      );
      showErrorToast(
        "Kunne ikke logge på SharePoint/OneDrive til upload. Prøv at logge ud og ind igen."
      );
      return null;
    }

    let uploadUrl: string;

    if (selectedFolder) {
      const basePath = selectedFolder.siteId
        ? `https://graph.microsoft.com/v1.0/sites/${selectedFolder.siteId}/drive/items/${selectedFolder.id}`
        : `https://graph.microsoft.com/v1.0/drives/${selectedFolder.driveId}/items/${selectedFolder.id}`;

      uploadUrl = `${basePath}:/${file.name}:/content`;
    } else {
      // No selected folder → upload to user's OneDrive root
      uploadUrl = `https://graph.microsoft.com/v1.0/me/drive/root:/${file.name}:/content`;
    }

    const response = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": file.type,
      },
      body: file,
    });

    if (!response.ok) {
      const errorBodyText = await response.text().catch(() => "");

      let graphErrorCode: string | undefined;
      let graphErrorMessage: string | undefined;
      let graphClientRequestId: string | undefined;
      if (errorBodyText) {
        try {
          const parsed = JSON.parse(errorBodyText) as {
            error?: {
              code?: string;
              message?: string;
              innerError?: Record<string, unknown>;
            };
          };
          const graphErr = parsed.error;
          if (graphErr) {
            graphErrorCode =
              typeof graphErr.code === "string" ? graphErr.code : undefined;
            graphErrorMessage =
              typeof graphErr.message === "string"
                ? graphErr.message.slice(0, 200)
                : undefined;
            const inner = graphErr.innerError;
            const rid =
              inner && typeof inner["client-request-id"] === "string"
                ? inner["client-request-id"]
                : undefined;
            graphClientRequestId = rid;
          }
        } catch {
          // Do not log raw body — may contain user/content details
        }
      }

      logger.error(
        "Failed to upload file to SharePoint/OneDrive via Graph API",
        {
          operation,
          fileSize: file.size,
          status: response.status,
          statusText: response.statusText,
          graphErrorCode,
          graphErrorMessage,
          graphClientRequestId,
          targetFolderId: selectedFolder?.id,
          targetSiteId: selectedFolder?.siteId,
          targetDriveId: selectedFolder?.driveId,
        }
      );

      if (response.status === 401 || response.status === 403) {
        showErrorToast(
          `Du har ikke rettigheder til at uploade "${file.name}" til den valgte SharePoint/OneDrive-placering.`
        );
      } else if (response.status === 404) {
        showErrorToast(
          "Den valgte SharePoint/OneDrive-mappe blev ikke fundet. Vælg en anden destination."
        );
      } else if (response.status === 409) {
        showErrorToast(
          `En fil med navnet "${file.name}" findes allerede på den valgte placering.`
        );
      } else if (response.status === 413 || response.status === 507) {
        showErrorToast(
          `Filen "${file.name}" er for stor eller der er ikke nok plads i SharePoint/OneDrive.`
        );
      } else {
        showErrorToast(
          `Kunne ikke uploade "${file.name}" til SharePoint/OneDrive.`
        );
      }
      return null;
    }

    return response.json();
  };

  const openSharePointDocumentInWordOffline = async (
    driveId: string,
    itemId: string
  ) => {
    const operation = "openSharePointDocumentInWordOffline";
    try {
      if (!driveId || !itemId) {
        logger.warn("Missing driveId or itemId for SharePoint edit", {
          operation,
          hasDriveId: Boolean(driveId),
          hasItemId: Boolean(itemId),
        });
        showErrorToast("Dokumentet mangler SharePoint/OneDrive oplysninger");
        return;
      }

      const accessToken = await getToken({
        type: "Graph",
        resource: "https://graph.microsoft.com",
        scopes: ["Files.Read.All"],
      });

      // Hent både webUrl og webDavUrl
      const metadataResponse = await fetch(
        `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}?$select=webUrl,webDavUrl,name`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!metadataResponse.ok) {
        if (metadataResponse.status === 403) {
          logger.warn("Graph API denied access to document metadata", {
            operation,
            status: metadataResponse.status,
            driveId,
            itemId,
          });
          showErrorToast("Du har ikke adgang til dette dokument");
        } else if (metadataResponse.status === 404) {
          logger.warn("Document not found in SharePoint/OneDrive", {
            operation,
            status: metadataResponse.status,
            driveId,
            itemId,
          });
          showErrorToast("Dokumentet blev ikke fundet i SharePoint/OneDrive");
        } else {
          logger.error("Failed to fetch document metadata from Graph API", {
            operation,
            status: metadataResponse.status,
            statusText: metadataResponse.statusText,
            driveId,
            itemId,
          });
          showErrorToast("Kunne ikke hente dokumentets link");
        }
        return;
      }

      const metadata = await metadataResponse.json();

      // Præference: brug webDavUrl – det er den rene fil-URL i biblioteket
      let fileUrl: string | undefined = metadata.webDavUrl;

      if (!fileUrl) {
        logger.error("webDavUrl missing in Graph API metadata response", {
          operation,
          driveId,
          itemId,
          metadataKeys: Object.keys(metadata ?? {}),
        });
        showErrorToast("Kunne ikke finde dokumentets direkte URL");
        return;
      }

      // webDavUrl fra Microsoft Graph API er allerede URL-encoded.
      // For at undgå dobbelt-encoding og problemer med mellemrum i filnavne,
      // dekoder vi først og encoder derefter igen for at sikre korrekt formatering.
      // Dette sikrer at alle specialtegn (inkl. mellemrum) er korrekt encoded.
      try {
        // Dekod URL først for at få den originale URL
        const decodedUrl = decodeURIComponent(fileUrl);
        // Encod igen for at sikre korrekt formatering til Word protocol handler
        // encodeURI vil korrekt encode mellemrum som %20
        fileUrl = encodeURI(decodedUrl);
      } catch (error) {
        // Hvis dekodning fejler, kan det betyde at URL'en ikke er encoded eller
        // har et andet format. Prøv at encode den direkte.
        logger.warn("Could not decode webDavUrl, encoding directly", {
          operation,
          driveId,
          itemId,
          error,
        });
        fileUrl = encodeURI(fileUrl);
      }

      // 'ofe' = Open For Edit → Word åbner det rigtige dokument i biblioteket.
      const wordProtocolUrl = `ms-word:ofe|u|${fileUrl}`;

      const link = document.createElement("a");
      link.href = wordProtocolUrl;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      logger.error("Unhandled error opening document in Word Offline", {
        operation,
        error,
        driveId,
        itemId,
      });
      showErrorToast("Kunne ikke åbne dokumentet");
    }
  };

  const openSharePointDocumentInWordOnline = async (
    driveId: string,
    itemId: string
  ) => {
    const operation = "openSharePointDocumentInWordOnline";
    try {
      if (!driveId || !itemId) {
        logger.warn("Missing driveId or itemId for SharePoint edit", {
          operation,
          hasDriveId: Boolean(driveId),
          hasItemId: Boolean(itemId),
        });
        showErrorToast("Dokumentet mangler SharePoint/OneDrive oplysninger");
        return;
      }

      const accessToken = await getToken({
        type: "Graph",
        resource: "https://graph.microsoft.com",
        scopes: ["Files.Read.All"],
      });

      const response = await fetch(
        `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}?$select=webUrl`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 403) {
          logger.warn("Graph API denied access to document", {
            operation,
            status: response.status,
            driveId,
            itemId,
          });
          showErrorToast("Du har ikke adgang til dette dokument");
        } else if (response.status === 404) {
          logger.warn("Document not found in SharePoint/OneDrive", {
            operation,
            status: response.status,
            driveId,
            itemId,
          });
          showErrorToast("Dokumentet blev ikke fundet i SharePoint/OneDrive");
        } else {
          logger.error("Failed to fetch document URL from Graph API", {
            operation,
            status: response.status,
            statusText: response.statusText,
            driveId,
            itemId,
          });
          showErrorToast("Kunne ikke hente dokumentets link");
        }
        return;
      }

      const data = await response.json();
      const webUrl = data.webUrl;
      if (webUrl) {
        window.open(webUrl, "_blank", "noopener,noreferrer");
      } else {
        logger.error("webUrl missing in Graph API response", {
          operation,
          driveId,
          itemId,
        });
        showErrorToast("Kunne ikke finde dokumentets link");
      }
    } catch (error) {
      logger.error("Unhandled error opening document in Word Online", {
        operation,
        error,
        driveId,
        itemId,
      });
      showErrorToast("Kunne ikke åbne dokumentet");
    }
  };

  // Warm MSAL up so the synchronous account check in `launchPicker` sees the
  // cache by the time the user clicks; errors are logged by ensureInitialized.
  useEffect(() => {
    ensureInitialized().catch(() => undefined);
  }, []);

  // `messageListener` is recreated every render; the port must always call the
  // latest closure, and cleanup must remove the very function it registered.
  const messageListenerRef = useRef(messageListener);
  messageListenerRef.current = messageListener;

  useEffect(() => {
    const portListener = (event: MessageEvent) => {
      void messageListenerRef.current(event);
    };

    const messageHandler = (event: MessageEvent) => {
      if (!event.source || event.source !== pickerWindow.current) return;

      const message = event.data as
        { type?: unknown; channelId?: unknown } | null | undefined;

      if (
        message?.type === "initialize" &&
        message.channelId === uuidRef.current
      ) {
        portRef.current?.close();
        portRef.current = event.ports[0];
        portRef.current.addEventListener("message", portListener);
        portRef.current.start();
        portRef.current.postMessage({ type: "activate" });
      }
    };

    window.addEventListener("message", messageHandler);

    return () => {
      window.removeEventListener("message", messageHandler);
      portRef.current?.removeEventListener("message", portListener);
      closePickerSession();
    };
    // Mount-only: closePickerSession only touches refs, so re-subscribing on
    // its identity change would just churn the window listener.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadFolderContentsForSync = async (
    driveId: string,
    siteId: string,
    folderId: string
  ): Promise<{
    files: DownloadedFileWithIds[];
    folders: DownloadedFolderWithIds[];
  }> => {
    return downloadFolderContents(
      {
        id: folderId,
        name: "",
        parentReference: {
          driveId,
          siteId,
        },
      },
      "",
      undefined
    );
  };

  const openCloudLocationChooser = (
    e: React.MouseEvent<HTMLElement> | undefined,
    pickerMode: PickerMode,
    purpose: PickerPurpose,
    options?: OpenCloudLocationChooserOptions
  ) => {
    e?.preventDefault();
    setPendingChooser({ pickerMode, purpose, onCancel: options?.onCancel });
  };

  const handleChooserSelect = (location: CloudLocation) => {
    if (!pendingChooser) return;
    const { pickerMode, purpose } = pendingChooser;
    setPendingChooser(null);
    void launchPicker(undefined, pickerMode, purpose, { location });
  };

  const handleChooserClose = () => {
    if (!pendingChooser) return;
    pendingChooser.onCancel?.();
    setPendingChooser(null);
  };

  const contextValue = useMemo(
    () => ({
      launchPicker,
      openCloudLocationChooser,
      fetchFileForSync,
      fetchGraphPdfForCloudItem,
      uploadFileToSharePoint,
      downloadQueue,
      enqueueDownloadBatch,
      dequeueDownloadBatch,
      selectedFolder,
      setSelectedFolder,
      openSharePointDocumentInWordOffline,
      openSharePointDocumentInWordOnline,
      downloadFolderContentsForSync,
    }),
    // The functions above are recreated every render; keep the value stable on
    // the state they read (`baseUrl` is captured by launchPicker).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [downloadQueue, selectedFolder, baseUrl]
  );

  return (
    <SharePointContext.Provider value={contextValue}>
      {children}
      <CloudLocationPickerDialog
        isOpen={pendingChooser !== null}
        onClose={handleChooserClose}
        onSelect={handleChooserSelect}
      />
    </SharePointContext.Provider>
  );
};

export default SharePointProvider;
